import { NetworkStatus, useReactiveVar } from "@apollo/client";
import React, { useEffect, useState } from "react";
import { Button, Col, Form, Row, Spinner } from "react-bootstrap";
import { PlusLg } from "react-bootstrap-icons";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import useGetUrlLocale from "../../hooks/useGetUrlLocale";
import { useGetOrganizerServiceInfoQuery } from "../../qraphql/event.hooks";
import {
  whiteLabelJRStep,
  whiteLabelValidJRSteps,
} from "../../qraphql/globalVar/state";
import { useOrganizerWhiteLabelQuery } from "../../qraphql/whiteLabel/whiteLabel.hooks";
import { DomainType, DomainTypeWhiteLabelEnum } from "../../types";
import DomainStatus from "./DomainStatus";
import DomainWrapper from "./DomainWrapper";
import SubDomainTicket from "./subDomainTicket";

enum ACTIVE_OPTION {
  TicketSubdomain = "TicketSubdomain",
  Domain = "Domain",
  Subdomain = "Subdomain",
}

export default function CustomDomain() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const urlLocale = useGetUrlLocale();
  const [activeOption, setActiveOption] = useState(
    ACTIVE_OPTION.TicketSubdomain
  );

  const whiteLabelValidJRStepsValue = useReactiveVar(whiteLabelValidJRSteps);

  const {
    data: whiteLabelInfo,
    loading: whiteLabelInfoLoading,
    refetch: refetchWhiteLabelInfo,
    networkStatus,
  } = useOrganizerWhiteLabelQuery({
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });
  const { data: serviceInfoData, loading: serviceInfoLoading } =
    useGetOrganizerServiceInfoQuery();
  const isProOrganizeer = serviceInfoData?.activeSubscription;

  useEffect(() => {
    if (!whiteLabelInfo) return;
    if (!whiteLabelInfo.organizer?.whiteLabel?.projectName) {
      navigate(`${urlLocale}/project-settings`);
      return;
    }
    const currentDomainType = whiteLabelInfo?.organizer?.whiteLabel?.domainType;
    if (currentDomainType === DomainType.TicketSubdomain) {
      setActiveOption(ACTIVE_OPTION.TicketSubdomain);
    } else if (currentDomainType === DomainType.Domain) {
      setActiveOption(ACTIVE_OPTION.Domain);
    } else if (currentDomainType === DomainType.Subdomain) {
      setActiveOption(ACTIVE_OPTION.Subdomain);
    }
  }, [whiteLabelInfo]);

  const setDomainTypeStep = (domainType: DomainTypeWhiteLabelEnum) => {
    whiteLabelValidJRSteps({
      ...whiteLabelValidJRStepsValue,
      step5: domainType,
    });
  };

  if (
    whiteLabelInfoLoading ||
    networkStatus === NetworkStatus.refetch ||
    serviceInfoLoading
  ) {
    return (
      <DomainWrapper>
        <div className="pt-5 d-flex justify-content-center">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      </DomainWrapper>
    );
  }

  return (
    <DomainWrapper>
      <div className="border border-secondary p-4 rounded mb-3">
        <Form.Check
          type="radio"
          id="subdomain"
          label={t("whiteLabel.Use subdomain me-ticket.com")}
          value={ACTIVE_OPTION.TicketSubdomain}
          checked={activeOption === ACTIVE_OPTION.TicketSubdomain}
          onChange={(e) => {
            setActiveOption(e.target.value as ACTIVE_OPTION);
            setDomainTypeStep(DomainTypeWhiteLabelEnum.SubDomain);
          }}
          className="fs-7"
        />
        {activeOption === ACTIVE_OPTION.TicketSubdomain && (
          <SubDomainTicket refetchWhiteLabelInfo={refetchWhiteLabelInfo} />
        )}
      </div>

      <div className="border border-secondary p-4 rounded mb-3">
        <Row className="align-items-center gy-2">
          <Col sm={8}>
            <Form.Check
              type="radio"
              id="myDomain"
              label={t("whiteLabel.Use my domain")}
              value={ACTIVE_OPTION.Domain}
              checked={activeOption === ACTIVE_OPTION.Domain}
              disabled={!isProOrganizeer}
              onChange={(e) => {
                setActiveOption(e.target.value as ACTIVE_OPTION);
                setDomainTypeStep(DomainTypeWhiteLabelEnum.MyDomain);
              }}
              className="fs-7"
            />
          </Col>
          {activeOption === ACTIVE_OPTION.Domain &&
            (!whiteLabelInfo.organizer?.whiteLabel?.domain?.length ||
              whiteLabelInfo.organizer?.whiteLabel?.domainType !==
                DomainType.Domain) && (
              <Col sm={4} className="ps-lg-5">
                <Link to={`${urlLocale}/custom-domain/add-domain`}>
                  <Button
                    variant="outline-success"
                    className="fw-normal p-2 w-100"
                    onClick={() => whiteLabelJRStep(7)}
                  >
                    <PlusLg className="me-2 mb-1" />{" "}
                    {t("whiteLabel.Add Domain")}
                  </Button>
                </Link>
              </Col>
            )}
        </Row>
        {whiteLabelInfo.organizer?.whiteLabel?.domainType ===
          DomainType.Domain &&
          whiteLabelInfo.organizer?.whiteLabel?.domain?.length && (
            <DomainStatus refetchWhiteLabelInfo={refetchWhiteLabelInfo} />
          )}
      </div>

      <div className="border border-secondary p-4 rounded mb-5">
        <Row className="align-items-center gy-2">
          <Col sm={8}>
            <Form.Check
              type="radio"
              id="mySubdomain"
              label={t("whiteLabel.Use my subdomain")}
              value={ACTIVE_OPTION.Subdomain}
              checked={activeOption === ACTIVE_OPTION.Subdomain}
              disabled={!isProOrganizeer}
              onChange={(e) => {
                setActiveOption(e.target.value as ACTIVE_OPTION);
                setDomainTypeStep(DomainTypeWhiteLabelEnum.MySubDomain);
              }}
              className="fs-7"
            />
          </Col>
          {activeOption === ACTIVE_OPTION.Subdomain &&
            (!whiteLabelInfo.organizer?.whiteLabel?.domain?.length ||
              whiteLabelInfo.organizer?.whiteLabel?.domainType !==
                DomainType.Subdomain) && (
              <Col sm={4} className="ps-lg-5">
                <Link to={`${urlLocale}/custom-domain/add-subdomain`}>
                  <Button
                    variant="outline-success"
                    className="fw-normal p-2 w-100"
                    onClick={() => whiteLabelJRStep(7)}
                  >
                    <PlusLg className="me-2 mb-1" />{" "}
                    {t("whiteLabel.Add Subdomain")}
                  </Button>
                </Link>
              </Col>
            )}
        </Row>
        {whiteLabelInfo.organizer?.whiteLabel?.domainType ===
          DomainType.Subdomain &&
          whiteLabelInfo.organizer?.whiteLabel?.domain?.length && (
            <DomainStatus
              refetchWhiteLabelInfo={refetchWhiteLabelInfo}
              isSubdomainMode
            />
          )}
      </div>
    </DomainWrapper>
  );
}
